import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useDestinationSearch } from 'frontend/features/hotels/hooks/useDestinationSearch';
import { validateHotelSearch } from 'frontend/features/hotels/utils/validateHotelSearch';
import { MAX_CHILD_AGE } from 'frontend/features/hotels/utils/childrenAgesParam';
import { AutocompleteField } from '../shared/AutocompleteField';
import { CounterPopoverField } from '../shared/CounterPopoverField';
import { DateRangeField } from '../shared/DateRangeField';
import { FieldShell } from '../shared/FieldShell';
import { formatLocalDate, parseLocalDate } from '../shared/dateValues';
import {
  EMPTY_HOTEL_SEARCH,
  buildHotelSearchQuery,
  parseHotelSearchValues,
} from './hotelSearchQuery';
import type { DestinationSuggestion } from 'frontend/features/hotels/api/hotels.types';
import type { HotelSearchFormValues } from 'frontend/features/hotels/types/hotels.presentation';
import type { SearcherFormProps } from '../types';
import '../shared/SearchFields.css';

const MIN_DESTINATION_CHARS = 3;
const MAX_GUESTS = 12;
const MAX_ROOMS = 5;
const CHILD_AGE_OPTIONS = Array.from({ length: MAX_CHILD_AGE + 1 }, (_, age) => age);

/** A destination rebuilt from a results URL: its label is the text the URL already carries. */
type SelectedDestination = DestinationSuggestion & { label?: string };

function destinationLabel(destination: SelectedDestination): string {
  return (
    destination.label ??
    `${destination.name}, ${destination.countryName ?? destination.countryCode}`
  );
}

/**
 * The autocomplete shows the selected suggestion, so a search opened from a link or a reload
 * needs one rebuilt from the URL (id + label) — otherwise the destination input stays empty
 * while dates and guests are filled.
 */
function selectionFromValues(values: HotelSearchFormValues): SelectedDestination | null {
  if (!values.destinationId || !values.destination.trim()) return null;
  return {
    id: values.destinationId,
    name: values.destination,
    countryCode: values.countryCode ?? '',
    label: values.destination,
  };
}

function guestsPreview(adults: number, children: number, rooms: number): string {
  const guests = adults + children;
  const guestText = guests === 1 ? '1 huésped' : `${guests} huéspedes`;
  const roomText = rooms === 1 ? '1 habitación' : `${rooms} habitaciones`;

  return `${guestText} · ${roomText}`;
}

/**
 * Real hotels search for the branding hero's search card.
 *
 * A hero-native form rather than a wrapper around the standalone
 * `features/hotels` search UI: it reuses that feature's destination typeahead,
 * validation and query contract, but renders through this feature's fields so
 * every searcher in the card looks like one component.
 */
const HotelSearchForm: React.FC<SearcherFormProps> = ({
  isMobile = false,
  onSearchComplete,
  onSearchStart,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { registerOnSearch } = useGlobal();

  const urlValues = useMemo(
    () =>
      pathname.replace(/\/$/, '') === ROUTE_KEYS.BUSCAR_X_HOTELES && search
        ? parseHotelSearchValues(search)
        : EMPTY_HOTEL_SEARCH,
    [pathname, search],
  );

  const [values, setValues] = useState<HotelSearchFormValues>(urlValues);
  const [selected, setSelected] = useState<SelectedDestination | null>(() =>
    selectionFromValues(urlValues),
  );
  const [term, setTerm] = useState('');
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formMessage, setFormMessage] = useState('');
  const refreshTokenRef = useRef(0);

  useEffect(() => {
    setValues(urlValues);
    setSelected(selectionFromValues(urlValues));
  }, [urlValues]);

  const termForApi = term.trim().length >= MIN_DESTINATION_CHARS ? term.trim() : '';
  const destinations = useDestinationSearch(termForApi);

  const checkIn = parseLocalDate(values.checkIn);
  const checkOut = parseLocalDate(values.checkOut);

  const selectDestination = (destination: DestinationSuggestion | null) => {
    setSelected(destination);
    setValues((prev) => ({
      ...prev,
      countryCode: destination?.countryCode,
      destination: destination ? destinationLabel(destination) : '',
      destinationId: destination?.id,
    }));
    setErrors((prev) => ({ ...prev, destination: undefined }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = validateHotelSearch(values);

    if (!result.isValid) {
      const nextErrors: Partial<Record<string, string>> = {};
      if (result.errors.destination) nextErrors.destination = t(result.errors.destination.message);
      if (result.errors.checkIn || result.errors.checkOut) {
        nextErrors.dates = t((result.errors.checkIn ?? result.errors.checkOut).message);
      }
      if (result.errors.adults || result.errors.children) {
        nextErrors.guests = t((result.errors.adults ?? result.errors.children).message);
      }
      if (result.errors.rooms) nextErrors.guests = t(result.errors.rooms.message);

      setErrors(nextErrors);
      setFormMessage(Object.values(nextErrors).filter(Boolean).join(' '));
      return;
    }

    setErrors({});
    setFormMessage('');
    onSearchStart?.();

    refreshTokenRef.current += 1;
    history.push(
      `${ROUTE_KEYS.BUSCAR_X_HOTELES}${buildHotelSearchQuery(values, {
        includeRefresh: true,
        refreshToken: refreshTokenRef.current,
      })}`,
    );
    registerOnSearch?.();
    onSearchComplete?.();
  };

  const adults = values.adults;
  const children = values.children ?? 0;
  const rooms = values.rooms ?? 1;
  const childrenAges = values.childrenAges ?? [];
  const remainingGuests = MAX_GUESTS - (adults + children);

  const updateCounter = (key: string, quantity: number) => {
    setValues((prev) => {
      const next = { ...prev, [key]: quantity };
      // One age slot per child: keep the ages already chosen, drop/pad the rest.
      next.childrenAges = Array.from(
        { length: next.children ?? 0 },
        (_, i) => prev.childrenAges?.[i],
      );
      return next;
    });
    setErrors((prev) => ({ ...prev, guests: undefined }));
  };

  const updateChildAge = (index: number, rawValue: string) => {
    setValues((prev) => {
      const ages = Array.from({ length: prev.children ?? 0 }, (_, i) => prev.childrenAges?.[i]);
      ages[index] = rawValue === '' ? undefined : Number(rawValue);
      return { ...prev, childrenAges: ages };
    });
    setErrors((prev) => ({ ...prev, guests: undefined }));
  };

  const childrenAgesFields =
    children > 0 ? (
      <div className='searcher-ages'>
        <p className='searcher-ages__hint'>{t('hotels.form.childrenAgesHint')}</p>
        <div className='searcher-ages__grid'>
          {Array.from({ length: children }, (_, index) => {
            const age = childrenAges[index];
            const selectId = `hotel-child-age-${index}`;
            return (
              <label className='searcher-ages__field' htmlFor={selectId} key={selectId}>
                {t('hotels.form.childAgeLabel', { number: index + 1 })}
                <select
                  className={[
                    'searcher-ages__select',
                    errors.guests && age === undefined && 'searcher-ages__select--invalid',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  id={selectId}
                  onChange={(event) => updateChildAge(index, event.target.value)}
                  value={age === undefined ? '' : String(age)}
                >
                  <option disabled value=''>
                    {t('hotels.form.childAgePlaceholder')}
                  </option>
                  {CHILD_AGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t('hotels.form.childAgeOption', { count: option })}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <form
      aria-label='Buscador de hoteles'
      className={['searcher-form', 'searcher-form--3', isMobile && 'searcher-form--mobile']
        .filter(Boolean)
        .join(' ')}
      noValidate
      onSubmit={handleSubmit}
    >
      <FieldShell
        error={errors.destination}
        errorId='hotel-destination-error'
        glyph='⌖'
        htmlFor='hotel-destination'
        label='Destino'
      >
        <AutocompleteField<SelectedDestination>
          emptyText={
            termForApi ? 'Sin resultados' : `Escribí al menos ${MIN_DESTINATION_CHARS} letras`
          }
          errorId='hotel-destination-error'
          filterMode='none'
          getOptionKey={(destination) => destination.id}
          getOptionLabel={destinationLabel}
          hasError={Boolean(errors.destination)}
          id='hotel-destination'
          isLoading={destinations.status === 'loading'}
          loadingText='Buscando destinos…'
          onInputChange={setTerm}
          onSelect={selectDestination}
          options={destinations.suggestions}
          placeholder='Ciudad o zona'
          value={selected}
        />
      </FieldShell>

      <FieldShell
        error={errors.dates}
        errorId='hotel-dates-error'
        glyph='▦'
        htmlFor='hotel-dates'
        label='Fechas'
      >
        <DateRangeField
          endPlaceholder='Salida'
          errorId='hotel-dates-error'
          hasError={Boolean(errors.dates)}
          id='hotel-dates'
          onChange={({ endDate, startDate }) => {
            setValues((prev) => ({
              ...prev,
              checkIn: formatLocalDate(startDate),
              checkOut: formatLocalDate(endDate),
            }));
            setErrors((prev) => ({ ...prev, dates: undefined }));
          }}
          startPlaceholder='Entrada'
          triggerLabel='Fechas de la estadía'
          value={{ endDate: checkOut, startDate: checkIn }}
        />
      </FieldShell>

      <FieldShell
        error={errors.guests}
        errorId='hotel-guests-error'
        glyph='♙'
        htmlFor='hotel-guests'
        label='Huéspedes'
      >
        <CounterPopoverField
          counters={[
            { key: 'adults', label: 'Adultos', max: adults + remainingGuests, min: 1 },
            { key: 'children', label: 'Niños', max: children + remainingGuests, min: 0 },
            { key: 'rooms', label: 'Habitaciones', max: MAX_ROOMS, min: 1 },
          ]}
          errorId='hotel-guests-error'
          extraContent={childrenAgesFields}
          hasError={Boolean(errors.guests)}
          id='hotel-guests'
          onChange={updateCounter}
          previewText={guestsPreview(adults, children, rooms)}
          triggerLabel='Huéspedes y habitaciones'
          values={{ adults, children, rooms }}
        />
      </FieldShell>

      <button className='searcher-form__submit' type='submit'>
        Buscar
      </button>

      {formMessage && (
        <p aria-live='assertive' className='sr-only' role='alert'>
          {formMessage}
        </p>
      )}
    </form>
  );
};

export default HotelSearchForm;
